import { StarIcon } from '@chakra-ui/icons'
import {
  Avatar,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  Tooltip
} from '@chakra-ui/react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  CreateTicketEvaluationsDTO,
  TicketEvaluationModalData
} from '@/modules/tickets/domain/models/TicketEvaluations'
import { useCreateTicketEvaluationMutation } from '@/ui/hooks/mutations/useCreateTicketEvaluationMutation'

import { TicketTag } from '../../../components/TicketTag'

export default function CreateTicketEvaluationModal({
  ticketId,
  data,
  onClose
}: {
  ticketId: string
  data: TicketEvaluationModalData
  onClose: () => void
}) {
  const { t } = useTranslation()

  const form = useForm<CreateTicketEvaluationsDTO>({
    mode: 'onChange'
  })

  const [evaluation, description] = form.watch(['evaluation', 'description'])

  useEffect(() => {
    form.setValue('description', description?.trimStart() ?? '')
  }, [description])

  const descriptionMaxLength = 300

  const createTicketEvaluationMutation = useCreateTicketEvaluationMutation({
    ticketId: ticketId,
    onSuccess: () => {
      onClose()
    }
  })

  const colorByValue = (starValue: number) => {
    const colorByValueMap = {
      1: '#D5474C',
      2: '#D5474C99',
      3: '#F3C52199',
      4: '#F3C521CC',
      5: '#F3C521'
    }

    return evaluation && starValue <= evaluation
      ? colorByValueMap[evaluation]
      : '#DEE1E6'
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      isCentered
      closeOnEsc
      scrollBehavior="inside"
      size={'xl'}
    >
      <form
        onSubmit={form.handleSubmit((data) =>
          createTicketEvaluationMutation.mutate({
            evaluation: data.evaluation,
            description: data?.description?.trim() || null
          })
        )}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader>
            <Text fontSize={'24px'} fontWeight={'600'}>
              {t('ticket.evaluation.modalTitle')}
            </Text>
          </ModalHeader>
          <ModalBody>
            <Flex flexDir={'column'}>
              <Flex
                flexDir={'column'}
                border={'1px'}
                borderColor={'#F1F3F5'}
                borderRadius={'4px'}
                p={2}
                mb={2}
              >
                <Flex justify={'space-between'}>
                  <Text fontWeight={'500'}>
                    {t('tickets.ticketNumber', {
                      number: data.ticketNumber
                    })}
                  </Text>
                  <TicketTag status={'COMPLETED'} />
                </Flex>
                <Text
                  fontSize={'14px'}
                  textColor={'#343A40'}
                  my={2}
                  noOfLines={3}
                >
                  {data.ticketDescription}
                </Text>
                <Flex alignItems={'center'}>
                  <Text fontWeight={'500'} fontSize={'14px'} mr={2}>
                    {`${t('tickets.attendant')}:`}
                  </Text>
                  <Avatar
                    name={data.attendant.username}
                    src={data.attendant.avatarUrl}
                    size="xs"
                    mr={1}
                  />
                  <Text fontSize={'12px'}>{data.attendant.username}</Text>
                </Flex>
                {data.patient && (
                  <Flex alignItems={'center'} mt={2}>
                    <Text fontWeight={'500'} fontSize={'14px'} mr={2}>
                      {t('tickets.ticketCreation.client.label')}:
                    </Text>
                    <Avatar
                      name={data.patient.username}
                      src={data.patient.avatarUrl}
                      size="xs"
                      mr={1}
                    />
                    <Text fontSize={'12px'}>{data.patient.username}</Text>
                  </Flex>
                )}
              </Flex>
              <FormControl
                isInvalid={!!form.formState.errors.evaluation?.message}
              >
                <FormLabel>
                  <Text
                    pr={'4px'}
                    mt={4}
                    textColor={'#495057'}
                    fontWeight={'500'}
                    mb={4}
                  >
                    {t('ticket.evaluation.rating.label')}
                  </Text>
                </FormLabel>
                <Flex gap={6}>
                  {([1, 2, 3, 4, 5] as const).map((starValue) => (
                    <Tooltip
                      key={starValue}
                      label={t(`ticket.evaluation.rating.values.${starValue}`)}
                      aria-label={t(
                        `ticket.evaluation.rating.values.${starValue}`
                      )}
                    >
                      <StarIcon
                        _hover={{ cursor: 'pointer' }}
                        color={colorByValue(starValue)}
                        onClick={(_) => form.setValue('evaluation', starValue)}
                        fontSize={'32px'}
                      />
                    </Tooltip>
                  ))}
                </Flex>
              </FormControl>
              <FormControl
                isInvalid={!!form.formState.errors.description?.message}
                display={'contents'}
              >
                <FormLabel mt={8}>
                  <Flex alignItems={'center'}>
                    <Text pr={'4px'} textColor={'#495057'} fontWeight={'500'}>
                      {t('ticket.evaluation.description.label')}
                    </Text>
                    <Text
                      textColor={'#495057'}
                      fontSize={'14px'}
                      fontWeight={'400'}
                    >
                      (
                      {evaluation && evaluation <= 2
                        ? t('general.form.required')
                        : t('general.form.optional')}
                      )
                    </Text>
                  </Flex>
                </FormLabel>
                <Textarea
                  h="140px"
                  maxLength={descriptionMaxLength}
                  placeholder={t('ticket.evaluation.description.hint')}
                  resize="none"
                  {...form.register('description')}
                />
                <Text
                  textAlign={'end'}
                  fontSize={'14px'}
                  textColor={
                    (description?.length ?? 0) < descriptionMaxLength
                      ? '#1A202C'
                      : 'red'
                  }
                >
                  {description?.length ?? 0}/{descriptionMaxLength}
                </Text>

                {(description?.length ?? 0) >= descriptionMaxLength && (
                  <Text fontSize={'14px'} textColor={'red'}>
                    {t('general.form.maxCaracters', {
                      max: descriptionMaxLength
                    })}
                  </Text>
                )}
                {evaluation && evaluation <= 2 && description === '' && (
                  <Text fontSize={'14px'} textColor={'red'}>
                    {t('general.form.required')}
                  </Text>
                )}
              </FormControl>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button
              isLoading={createTicketEvaluationMutation.isPending}
              isDisabled={
                createTicketEvaluationMutation.isPending ||
                !evaluation ||
                (evaluation <= 2 && !description)
              }
              w={'full'}
              backgroundColor="#2196F3"
              _hover={{ opacity: 0.6 }}
              fontSize={'16px'}
              fontWeight={'500'}
              textColor={'#F5F5FF'}
              type="submit"
            >
              <Text>{t('ticket.evaluation.save')}</Text>
            </Button>
          </ModalFooter>
        </ModalContent>
      </form>
    </Modal>
  )
}
